import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { EyeIcon } from '@heroicons/react/24/outline'
import {
  AggregateMinAvgMax,
  AggregateResult,
  AggregateRow
} from '../../utils/weldParameterAggregator'
import { VisibleColumns } from '../../utils/weldParametersColumnLayout'
import ParameterMatrix, { FilterApi, FilterTarget } from './ParameterMatrix'
import {
  applyWeldParameterFilters,
  buildFilterOptions,
  EMPTY_WP_FILTER,
  isFilterActive,
  readFilters,
  targetKey,
  writeFilters,
  type WeldParameterFilterRow,
  type WeldParameterFilterState,
  type WeldParameterFiltersMap
} from '../../utils/weldParametersFilters'
import { resolvePreheatCellsForPass } from '../../utils/preheatInterpassForPass'
import { applyPqrToleranceToTriple } from '../../utils/pqr/applyPqrTolerance'
import type { PqrLimitsMode } from '../../utils/pqr/applyPqrTolerance'
import { renderMatrixNumber } from './weldParameters/weldParameterCellDisplay'
import { renderPassWithIwmSwapBadge } from './weldParameters/IwmRootSwappedBadge'
import { applyIwmRootSwapToAggregate } from '../../utils/pqr/applyIwmRootSwapToWeldParams'

interface WeldParametersUploadedViewProps {
  aggregate: AggregateResult
  /** Pass labels in their display order (e.g. ['Root', 'Hot', 'Fill 1', ...]). */
  passLabels: string[]
  /** Per-(param, agg) visibility. Defaults to the issue-03 live columns. */
  visibleColumns?: VisibleColumns
  /**
   * When provided, a sticky leftmost actions column is rendered with an eye
   * icon per pass row. Clicking the eye on a pass that has samples invokes
   * this callback with the pass label. The Pass Analytics page is then
   * responsible for navigation. Passes with `sampleCount === 0` (or passes
   * that don't appear in the aggregate at all) render the icon disabled.
   */
  onPassView?: (pass: string) => void
  /**
   * Optional opaque key used to scope filter persistence in `sessionStorage`.
   * The parent (`WeldParameters`) builds this from `(project, pipeSet, weld)`
   * so each weld keeps its own filter draft for the session. When omitted,
   * filters live in component state only.
   */
  filtersStorageKey?: string
  /** Pipe-set `sections.preheat` — read-only source for the Preheat / Interpass column. */
  preheatSection?: Record<string, unknown> | null
  /** Per-weld floor-sheet preheat/interpass overrides keyed by pass label. */
  preheatInterpassByPass?: Record<string, { min: string; max: string; avg?: string }> | null
  /**
   * When true, render a disabled Pass Analytics eye per row (manual ALX uploads
   * have no per-sample telemetry).
   */
  passAnalyticsUnavailable?: boolean
  /** Per-weld tolerance boundary % (default 0). */
  toleranceBoundary?: number
  pqrLimitsMode?: PqrLimitsMode
  /** When true, Root pass shows a Swapped badge (Excel Root CW↔CCW). */
  swapIwmBugType?: boolean
}

const NULL_TRIPLE: AggregateMinAvgMax = { min: null, avg: null, max: null }

/**
 * Read-only display of Min/Avg/Max per pass computed from a TXT upload.
 * Layout chrome lives in `ParameterMatrix`; this component only supplies
 * the read-only torch pill and value cell renderers.
 */
export default function WeldParametersUploadedView({
  aggregate,
  passLabels,
  visibleColumns,
  onPassView,
  filtersStorageKey,
  swapIwmBugType = false,
  preheatSection,
  preheatInterpassByPass,
  passAnalyticsUnavailable = false,
  toleranceBoundary = 0,
  pqrLimitsMode = 'minmax'
}: WeldParametersUploadedViewProps) {
  const displayAggregate = useMemo(
    () => applyIwmRootSwapToAggregate(aggregate, swapIwmBugType),
    [aggregate, swapIwmBugType]
  )
  const byPass = useMemo(() => indexAggregate(displayAggregate), [displayAggregate])
  const resolvedPreheatUnit =
    String(preheatSection?.preheatUnit ?? '°F').trim() || '°F'

  const filterRowsByPass = useMemo<Map<string, WeldParameterFilterRow>>(() => {
    const map = new Map<string, WeldParameterFilterRow>()
    for (const pass of passLabels) {
      const row = byPass.get(pass)
      map.set(pass, {
        pass,
        torch: row && row.torches.length > 0 ? row.torches.join(', ') : null,
        values: row?.values ?? {}
      })
    }
    return map
  }, [byPass, passLabels])

  const filterRows = useMemo(
    () => Array.from(filterRowsByPass.values()),
    [filterRowsByPass]
  )

  const [filters, setFilters] = useState<WeldParameterFiltersMap>(() =>
    readFilters(filtersStorageKey ?? '')
  )
  useEffect(() => {
    setFilters(readFilters(filtersStorageKey ?? ''))
  }, [filtersStorageKey])

  const persistFilters = useCallback(
    (next: WeldParameterFiltersMap) => {
      writeFilters(filtersStorageKey ?? '', next)
    },
    [filtersStorageKey]
  )

  const updateFilter = useCallback(
    (target: FilterTarget, updater: (prev: WeldParameterFilterState) => WeldParameterFilterState) => {
      setFilters((prev) => {
        const key = targetKey(target)
        const current = prev[key] ?? EMPTY_WP_FILTER
        const nextEntry = updater(current)
        const next = { ...prev, [key]: nextEntry }
        if (!isFilterActive(nextEntry)) {
          delete next[key]
        }
        persistFilters(next)
        return next
      })
    },
    [persistFilters]
  )

  const filterApi: FilterApi = useMemo(
    () => ({
      getOptions: (target) => buildFilterOptions(filterRows, target),
      getValue: (target) => filters[targetKey(target)]?.values ?? null,
      getText: (target) => filters[targetKey(target)]?.text ?? '',
      onChange: (target, values) =>
        updateFilter(target, (prev) => ({ ...prev, values })),
      onTextChange: (target, text) =>
        updateFilter(target, (prev) => ({ ...prev, text }))
    }),
    [filterRows, filters, updateFilter]
  )

  const filteredPassLabels = useMemo(
    () => applyWeldParameterFilters(passLabels, filterRowsByPass, filters),
    [passLabels, filterRowsByPass, filters]
  )

  const renderRowActions = onPassView
    ? (pass: string) => {
        const row = byPass.get(pass)
        const count = row?.sampleCount ?? 0
        const disabled = count <= 0
        const title = disabled
          ? 'No telemetry recorded for this pass'
          : `View ${count.toLocaleString()} samples for ${pass}`
        return (
          <button
            type="button"
            data-testid={`wp-pass-view-${pass}`}
            disabled={disabled}
            onClick={() => onPassView(pass)}
            title={title}
            aria-label={title}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[#E2E5EA] bg-white text-heritage-grey transition-all duration-150 hover:border-hero-red-20 hover:bg-hero-red-10/60 hover:text-hero-red hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-[#E2E5EA] disabled:hover:bg-white disabled:hover:text-heritage-grey disabled:hover:shadow-none"
          >
            <EyeIcon className="h-4 w-4" aria-hidden />
          </button>
        )
      }
    : passAnalyticsUnavailable
      ? (pass: string) => {
          const title = 'Pass Analytics not available for manual ALX uploads'
          return (
            <button
              type="button"
              data-testid={`wp-pass-view-${pass}`}
              disabled
              title={title}
              aria-label={title}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[#E2E5EA] bg-white text-heritage-grey opacity-40 cursor-not-allowed"
            >
              <EyeIcon className="h-4 w-4" aria-hidden />
            </button>
          )
        }
      : undefined

  return (
    <ParameterMatrix
      passLabels={filteredPassLabels}
      visibleColumns={visibleColumns}
      preheatUnit={resolvedPreheatUnit}
      selectable={false}
      filterApi={filterApi}
      renderRowActions={renderRowActions}
      renderPassCell={(pass) => renderPassWithIwmSwapBadge(pass, swapIwmBugType)}
      renderTorchCell={(pass) => {
        const row = byPass.get(pass)
        if (!row || row.torches.length === 0) {
          return <span className="text-[#C9CDD4]">—</span>
        }
        return (
          <span className="inline-flex flex-wrap items-center gap-1.5">
            {row.torches.map((t, i) => (
              <React.Fragment key={t}>
                <TorchPill torch={t} />
                {i < row.torches.length - 1 ? (
                  <span className="text-[#C9CDD4]">,&nbsp;</span>
                ) : null}
              </React.Fragment>
            ))}
          </span>
        )
      }}
      renderValueCell={(pass, col, agg) => {
        if (col.key === 'preheat') {
          const weldOverride = preheatInterpassByPass?.[pass]
          const cells = resolvePreheatCellsForPass(preheatSection, pass, weldOverride)
          const triple = applyPqrToleranceToTriple(
            {
              min: parsePreheatNumber(cells.min),
              avg: cells.avg,
              max: parsePreheatNumber(cells.max)
            },
            toleranceBoundary,
            pqrLimitsMode
          )
          const content =
            agg === 'avg'
              ? renderMatrixNumber(triple.avg)
              : renderMatrixNumber(agg === 'min' ? triple.min : triple.max)
          return (
            <span data-testid={`wp-cell-${pass}-${col.key}-${agg}`}>{content}</span>
          )
        }
        const row = byPass.get(pass)
        const stats = row?.values?.[col.key] ?? NULL_TRIPLE
        const adjusted = applyPqrToleranceToTriple(stats, toleranceBoundary, pqrLimitsMode)
        return (
          <span data-testid={`wp-cell-${pass}-${col.key}-${agg}`}>
            {fmtCell(adjusted[agg])}
          </span>
        )
      }}
    />
  )
}

function indexAggregate(aggregate: AggregateResult): Map<string, AggregateRow> {
  const map = new Map<string, AggregateRow>()
  for (const row of aggregate.rows) {
    map.set(row.pass, row)
  }
  return map
}

function fmtCell(value: number | null | undefined): React.ReactNode {
  return renderMatrixNumber(value)
}

function parsePreheatNumber(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : null
}

function TorchPill({ torch }: { torch: 'Lead' | 'Trail' }) {
  const cls =
    torch === 'Lead'
      ? 'bg-accent-50 text-accent-700 ring-accent-200'
      : 'bg-warning-50 text-warning-700 ring-warning-200'
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${cls}`}
    >
      {torch}
    </span>
  )
}
